<template>
  <div>
    <div class="grid grid-cols-3 gap-2 mx-3">
      <CustomPanelCard
        v-for="panel in customPanelList"
        :id="panel.bundleId"
        :key="panel.bundleId"
        :panel="panel"
        :name="panel.bundleName"
        :selectProp="selectProp"
        :is-fav-panel-open="orderTestSupportObj.isFavPanelOpen"
        @click="handleClick"
      />
    </div>
    <MultipleXChooseYSelectionDialog
      :isShowDialog="isVisible"
      :title="currentSelectedPanel.bundleName"
      itemCategory="custom panel"
      :xChooseYPanelList="xChooseYPanelList"
      @done="completeXChooseYPanels"
      @close="isVisible = false"
    />
    <AddOnUnableSelectDialog
      :is-show-dialog="addOnUnableSelectDialogVisible"
      :test-name="currentSelectedPanel.bundleName"
      :unable-select-reason="addOnUnableSelectReason"
      @close="addOnUnableSelectDialogVisible = false"
    />
    <DuplicateGeneticTestDialog
      v-if="showDuplicateGeneticTestDialog"
      :is-show-dialog="showDuplicateGeneticTestDialog"
      :test-name-list="getDuplicateTestNames()"
      :show-remove-button="false"
      @close="showDuplicateGeneticTestDialog = false"
      @confirm="handleDuplicateGeneticTestConfirm"
    />
  </div>
</template>

<script>
import CustomPanelCard from "./CustomPanelCard.vue";
import MultipleXChooseYSelectionDialog from "../MultipleXChooseYSelectionDialog.vue";
import AddOnUnableSelectDialog from '../order-test-panel/order-test-tabs/AddOnUnableSelectDialog.vue'
import DuplicateGeneticTestDialog from "../order-test-panel/order-test-tabs/DuplicateGeneticTestDialog.vue";
import { mapGetters, mapState } from 'vuex';
import { addSelectedCustomPanel, removeSelectedCustomPanel } from '@/utils/va-order-test/testAndPanelSelection/customPanelSelection';
import { getOneTimePurchaseEligibility } from "@/views/va-orderTest/order-page/patientPurchaseEligibility.js";

export default {
  components: {
    CustomPanelCard,
    MultipleXChooseYSelectionDialog,
    AddOnUnableSelectDialog,
    DuplicateGeneticTestDialog,
  },
  props: {
    orderDraft: {
      type: Object,
      default: () => {},
    },
    selectProp: {
      type: String,
      default: "isSelect"
    },
    orderTestSupportObj: {
      type: Object,
      default: () => {}
    },
    customPanelList: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isVisible: false,
      xChooseYPanelList: [],
      currentSelectedPanel : {},
      haveChosenDraftItems: false,
      addOnUnableSelectDialogVisible: false,
      showDuplicateGeneticTestDialog: false,
    };
  },
  computed: {
    ...mapState("orderTest", ["isAddOnOrder", "addOnFixedCustomPanel", "addOnRequiredMoreNoOfTubesTests"]),
    ...mapGetters("orderTest/tests", ["testList"]),
    ...mapGetters("orderTest/panels", ["bundleList"]),
    addOnUnableSelectReason() {
      return this.addOnUnableSelectReason || this.$t('OrderPage["Already Ordered"]');
    },

    // Cached eligibility data
    eligibilityData() {
      return getOneTimePurchaseEligibility();
    }
  },


  methods: {
    handleClick(panel) {
      this.currentSelectedPanel = panel;

      if(this.isAddOnOrder.length > 0) {
        if(this.addOnFixedCustomPanel.includes(panel.bundleId)) {
          this.addOnUnableSelectDialogVisible = true
          this.addOnUnableSelectReason = "Already Ordered"
          return
        }
        if(
          panel.bundleItemList.length === 0 ||
          panel.bundleItemList.map(i => i.itemId)
            .filter(x =>
            [300,371,339].includes(x) ||
              x === undefined ||
              this.addOnRequiredMoreNoOfTubesTests.map(i => i.id).includes(x)
            ).length > 0
        ) {
          this.addOnUnableSelectDialogVisible = true
          this.addOnUnableSelectReason = "Requires New Kit"
          return
        }
      }

      // Check genetic test duplicates
      if (this.isPanelContainsGeneticDuplicate(panel)) {
        this.showDuplicateGeneticTestDialog = true;
        return;
      }

      // get xChooseY panels in current selected panel
      this.xChooseYPanelList = this.getXChooseYPanelList(panel);
      if (this.xChooseYPanelList.length > 0) {
        if (!panel[this.selectProp]) {
          this.isVisible = true;
          this.xChooseYPanelList.forEach(p => {
            p.isComplete = false;
          });
        }
        else {
          removeSelectedCustomPanel(panel.bundleId, this.selectProp, this.orderTestSupportObj);
        }
      }
      else {
        panel[this.selectProp]
          ? removeSelectedCustomPanel(panel.bundleId, this.selectProp, this.orderTestSupportObj)
          : addSelectedCustomPanel(panel.bundleId, this.selectProp, this.orderTestSupportObj);
        this.isAddOnOrder.length > 0 && this.$store.dispatch("orderTest/addOnFixedVaVwTestTubeInfo", {root:panel,isFixedVaVw:'false',isSelect:panel[this.selectProp] ? 'true' : 'false'})
      }
    },

    isPanelContainsGeneticDuplicate(panel) {
      // If panel is already selected, allow deselecting
      if (panel[this.selectProp]) {
        return false;
      }

      const eligibilityData = this.eligibilityData;
      if (!eligibilityData || Object.keys(eligibilityData).length === 0) {
        return false;
      }

      // Check all test IDs in the panel
      if (panel.bundleItemList && panel.bundleItemList.length > 0) {
        for (const item of panel.bundleItemList) {
          if (item.itemType !== "official bundle") {
            // Direct test
            if (eligibilityData[item.itemId] === false) {
              return true;
            }
          } else {
            // Official bundle - check its testIdsInPanel
            const bundle = this.bundleList.find(b => b.bundleId === item.itemId);
            if (bundle && bundle.testIdsInPanel) {
              const hasDuplicate = bundle.testIdsInPanel.some(testId => {
                return typeof eligibilityData[testId] !== 'undefined' &&
                       eligibilityData[testId] === false;
              });
              if (hasDuplicate) {
                return true;
              }
            }
          }
        }
      }

      return false;
    },

    getDuplicateTestNames() {
      if (!this.currentSelectedPanel || !this.eligibilityData) {
        return [];
      }

      const duplicateTestIds = [];

      if (this.currentSelectedPanel.bundleItemList) {
        this.currentSelectedPanel.bundleItemList.forEach(item => {
          if (item.itemType !== "official bundle") {
            if (this.eligibilityData[item.itemId] === false) {
              duplicateTestIds.push(item.itemId);
            }
          } else {
            const bundle = this.bundleList.find(b => b.bundleId === item.itemId);
            if (bundle && bundle.testIdsInPanel) {
              bundle.testIdsInPanel.forEach(testId => {
                if (this.eligibilityData[testId] === false) {
                  duplicateTestIds.push(testId);
                }
              });
            }
          }
        });
      }

      return duplicateTestIds
        .map(testId => this.getTestNameById(testId));
    },

    getTestNameById(testId) {
      const test = this.testList.find(t => t.id === testId);
      return test ? test.testName : `Test ${testId}`;
    },

    handleDuplicateGeneticTestConfirm() {
      // User confirmed - just close the dialog, do nothing
      // Similar to PromoPanel behavior
      this.showDuplicateGeneticTestDialog = false;
    },

    getXChooseYPanelList(panel) {
      let officialPanelIds = panel.bundleItemList
        .filter(item => item.itemType === "official bundle")
        .map(item => item.itemId);
      let xChooseYPanels = this.bundleList.filter(bundle =>
        officialPanelIds.includes(bundle.bundleId) && bundle.quantity);
      return xChooseYPanels;
    },

    completeXChooseYPanels(finishedXChooseYs) {
      this.currentSelectedPanel.selectedXChooseYPanelList = finishedXChooseYs;
      this.$store.dispatch("orderTest/addSelectedCustomPanel", this.currentSelectedPanel);
      this.isVisible = false;
    }
  },
};
</script>

<style scoped>
.subtitle-text {
  color: #20A8C3;
  font-size: 14px;
  letter-spacing: 0.4px;
  line-height: 16px;
}

.button {
  border-radius: 4px;
  background-color: #20A8C3;
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.43px;
}

.done-icon {
  color: #0ac21f;
}
</style>
